/** A client address as the backend sends it (`actualAddress`); `location2` is the city. */
export type Address = {
  fullAddress?: string | null;
  postalCode?: string | null;
  location2?: string | null;
};

const joinPresent = (parts: (string | null | undefined)[], separator: string) =>
  parts.filter(Boolean).join(separator);

/**
 * Formats a client address on one line: the street, then the postal code and the city.
 * Parts that are missing are left out; returns `''` when there is no address.
 *
 * @example
 * formatAddress({ fullAddress: 'Vinohradská 1', postalCode: '12000', location2: 'Praha' });
 * // 'Vinohradská 1, 12000 Praha'
 */
export function formatAddress(address: Address | null | undefined): string {
  return joinPresent(
    [
      address?.fullAddress,
      joinPresent([address?.postalCode, address?.location2], ' '),
    ],
    ', ',
  );
}

/** A permanent-residence address from a BankID session. */
export type BankIdAddress = {
  street: string;
  buildingapartment: string | null;
  streetnumber: string | null;
  city: string;
  zipcode: string;
};

/**
 * Formats a BankID address on one line: the street with the building and
 * street numbers (`1234/56`), then the city and the postal code.
 * A missing house number is left out.
 *
 * @example
 * formatBankIdAddress({ street: 'Vinohradská', buildingapartment: '1234', streetnumber: '56', city: 'Praha', zipcode: '12000' });
 * // 'Vinohradská 1234/56, Praha 12000'
 */
export function formatBankIdAddress(address: BankIdAddress): string {
  const houseNumber = joinPresent(
    [address.buildingapartment, address.streetnumber],
    '/',
  );

  return joinPresent(
    [
      joinPresent([address.street, houseNumber], ' '),
      joinPresent([address.city, address.zipcode], ' '),
    ],
    ', ',
  );
}
